import { useEffect, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { EditButton, RecordContextProvider, ShowButton } from 'react-admin';
import HttpClient from '../../HttpClient';
import { formatSlotSize } from '../../utils/FormatSlotSize';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';

interface InputProps {
    customerId: number;
}

const ROWS_PER_PAGE = 10;

const columns: CommonListColumn[] = [
    { label: 'ID', field: 'id' },
    {
        label: 'Name',
        render: (row) => (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {row.name}
                {row._isSummary && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            px: '6px',
                            py: '2px',
                            borderRadius: '4px',
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 600,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Summary
                    </Box>
                )}
                {row._isHome && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            px: '6px',
                            py: '2px',
                            borderRadius: '4px',
                            backgroundColor: '#9c27b0',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 600,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Home
                    </Box>
                )}
            </Box>
        ),
    },
    { label: 'Price', field: 'price' },
    { label: 'Value', field: 'value' },
    { label: 'Coupon Type', field: 'couponType' },
    { label: 'Product Type', field: 'productType' },
    { label: 'No. of People', field: 'noOfPeople' },
    { label: 'Slot Size', render: (row) => formatSlotSize(row.slotSize) },
    { label: 'Days', field: 'numberOfDays' },
    { label: 'Min Order Value', field: 'minimumOrderValue' },
    { label: 'Max Discount', field: 'maximumDiscountAmount' },
    { label: 'Max Usage Limit', field: 'maximumUsageLimit' },
    {
        label: 'Active',
        render: (row) => (
            <Chip
                label={row.isActive ? 'Yes' : 'No'}
                size="small"
                color={row.isActive ? 'success' : 'default'}
            />
        ),
    },
    { label: 'Pack Tag', render: (row) => row.packTag ?? '—' },
    {
        label: '',
        stopRowClick: true,
        render: (row) => (
            <RecordContextProvider value={row}>
                <Box display="flex" gap={0.5}>
                    <ShowButton resource="CouponSku" />
                    <EditButton resource="CouponSku" />
                </Box>
            </RecordContextProvider>
        ),
    },
];

const CustomerCouponSkuTab = ({ customerId }: InputProps) => {
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Keep summary/home SKUs across page changes so badges always show
    const [summarySku, setSummarySku] = useState<any>(null);
    const [homeSku, setHomeSku] = useState<any>(null);

    const fetchPage = (pageIndex: number, existingSummarySku: any, existingHomeSku: any) => {
        setIsLoading(true);
        const offset = pageIndex * ROWS_PER_PAGE;
        HttpClient.get(`/centaurus/v1/discount-coupon-sku/customer/${customerId}/all`, {
            params: { limit: ROWS_PER_PAGE, offset },
        })
            .then((res: any) => {
                const data = res?.data;
                const rawSkus: any[] = data?.allSkus?.data ?? [];
                const serverTotal: number = data?.allSkus?.count ?? 0;

                const currentSummary = existingSummarySku ?? data?.summaryPageSku ?? null;
                const currentHome = existingHomeSku ?? data?.homePageOfferSku?.discountCouponSku ?? null;

                if (!existingSummarySku && data?.summaryPageSku) setSummarySku(data.summaryPageSku);
                if (!existingHomeSku && data?.homePageOfferSku?.discountCouponSku) setHomeSku(data.homePageOfferSku.discountCouponSku);

                const summaryId = currentSummary?.id != null ? Number(currentSummary.id) : null;
                const homeId = currentHome?.id != null ? Number(currentHome.id) : null;

                const enriched: any[] = rawSkus.map((sku) => ({
                    ...sku,
                    _isSummary: summaryId !== null && Number(sku.id) === summaryId,
                    _isHome: homeId !== null && Number(sku.id) === homeId,
                }));

                // Inject summary/home at top of page 0 if not already present
                if (pageIndex === 0) {
                    const idsInList = new Set(enriched.map((s) => Number(s.id)));

                    if (currentSummary && !idsInList.has(summaryId as number)) {
                        enriched.unshift({
                            ...currentSummary,
                            _isSummary: true,
                            _isHome: homeId !== null && Number(currentSummary.id) === homeId,
                        });
                        idsInList.add(Number(currentSummary.id));
                    }

                    if (currentHome && !idsInList.has(homeId as number)) {
                        enriched.unshift({ ...currentHome, _isSummary: false, _isHome: true });
                    }
                }

                setRows(enriched);
                setTotal(serverTotal);
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (!customerId) return;
        setPage(0);
        setSummarySku(null);
        setHomeSku(null);
        fetchPage(0, null, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        fetchPage(newPage, summarySku, homeSku);
    };

    return (
        <Box pt={2} px={1}>
            <CommonListTable
                columns={columns}
                data={rows}
                loading={isLoading}
                page={page}
                rowsPerPage={ROWS_PER_PAGE}
                total={total}
                onPageChange={handlePageChange}
                onRowsPerPageChange={() => {}}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                emptyText="No Coupon SKUs Found"
            />
        </Box>
    );
};

export default CustomerCouponSkuTab;
